import styled from "@emotion/styled";

import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import type { TaskSourceNote } from "@/pages/agent/utils/agent-task-source.utils";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";

export const AgentSourceNoteCard = ({
  note,
  onOpen,
}: {
  note: TaskSourceNote;
  onOpen: ({ noteId }: { noteId: string }) => void;
}) => {
  const title = decodeAgentDisplayText({ value: note.title || "Untitled note" });
  const secondaryTitle = decodeAgentDisplayText({ value: note.secondaryTitle || "Mem Note" });

  return (
    <StyledCardButton
      aria-label={`Open note ${title}`}
      type="button"
      onClick={() => onOpen({ noteId: note.id })}
    >
      <StyledIcon>
        <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Document} size={16} />
      </StyledIcon>
      <StyledTitleGroup>
        <StyledTitle
          color={MDSTextColor.Primary}
          numberOfLines={2}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          {title}
        </StyledTitle>
        <MDSText color={MDSTextColor.Tertiary} numberOfLines={1} variant={MDSTextVariant.TextXs}>
          {secondaryTitle} · Updated {formatAgentDate({ value: note.modifiedAt })}
        </MDSText>
      </StyledTitleGroup>
      <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.RightArrow} size={14} />
    </StyledCardButton>
  );
};

const StyledCardButton = styled.button`
  appearance: none;
  align-items: center;
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: inherit;
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 82px;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  width: 100%;

  &:focus-visible {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }
`;

const StyledIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const StyledTitleGroup = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledTitle = styled(MDSText)`
  overflow-wrap: anywhere;
`;
